import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, Trophy, Flame, Route as RouteIcon, Coins } from 'lucide-react';
import { requireLevel } from '@/lib/guards';
import { useCompany } from '@/lib/company-context';
import { useMemberStats } from '@/lib/queries/corporate';
import { StatCard } from '@/components/shared/stat-card';
import { StatCardSkeleton } from '@/components/shared/skeleton';
import { LevelBadge } from '@/components/shared/badge';
import { ErrorState } from '@/components/shared/error-state';
import { formatNumber } from '@/lib/format';

export const Route = createFileRoute('/app/team/$userId')({
  beforeLoad: requireLevel(5),
  component: MemberDetailPage,
});

function MemberDetailPage() {
  const { userId } = Route.useParams();
  const { company } = useCompany();
  const { data, isLoading, error, refetch } = useMemberStats(company.slug, userId);

  if (error) {
    return <ErrorState error={error} onRetry={() => void refetch()} />;
  }

  return (
    <div className="space-y-6">
      <Link
        to="/app/team"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Team
      </Link>

      {isLoading ? (
        <>
          <div className="rounded-xl border border-border bg-card p-5">
            <StatCardSkeleton />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </div>
        </>
      ) : data ? (
        <>
          {/* Profile Card */}
          <div className="rounded-xl border border-border bg-card p-5 flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center shrink-0">
              {data.member.avatar_url ? (
                <img
                  src={data.member.avatar_url}
                  alt=""
                  className="h-14 w-14 rounded-full object-cover"
                />
              ) : (
                <span className="text-lg font-medium text-muted-foreground">
                  {(data.member.display_name ?? 'U').charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-lg font-bold text-foreground truncate">
                {data.member.display_name ?? 'Unknown'}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <LevelBadge level={data.member.level} />
              </div>
            </div>
          </div>

          {/* Lifetime Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard
              icon={Flame}
              label="Lifetime Points"
              value={formatNumber(data.lifetime.points)}
            />
            <StatCard icon={RouteIcon} label="Trips" value={formatNumber(data.lifetime.trips)} />
            <StatCard icon={Coins} label="Coins" value={formatNumber(data.lifetime.coins)} />
            <StatCard icon={Trophy} label="Streaks" value={data.streaks?.length ?? 0} />
          </div>

          {/* Period Ranks */}
          {data.current_ranks?.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-5">
              <h2 className="text-base font-semibold text-foreground mb-3">Current Rankings</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {data.current_ranks.map(
                  (r: { period: string; rank: number; points: number; total_ranked: number }) => (
                    <div key={r.period} className="rounded-lg bg-muted/50 p-3 text-center">
                      <p className="text-xs text-muted-foreground capitalize">{r.period}</p>
                      <p className="text-lg font-bold text-foreground tabular-nums">
                        {r.rank ? `#${r.rank}` : '--'}
                      </p>
                      <p className="text-xs text-muted-foreground">{formatNumber(r.points)} pts</p>
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {/* Recent Activity */}
          {data.recent_activity?.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-5">
              <h2 className="text-base font-semibold text-foreground mb-3">Recent Activity</h2>
              <div className="space-y-2">
                {data.recent_activity.map(
                  (day: { date: string; trips: number; points: number; distance_m: number }) => (
                    <div key={day.date} className="flex items-center justify-between py-1.5">
                      <span className="text-sm text-muted-foreground">
                        {new Date(day.date).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                      <div className="flex items-center gap-4 text-sm tabular-nums">
                        <span className="text-foreground">{day.trips} trips</span>
                        <span className="text-muted-foreground">
                          {formatNumber(day.points)} pts
                        </span>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
